import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, forkJoin } from 'rxjs';
import { ServiceCodes } from 'src/app/models/enums/service-codes';
import { ServiceSubscription } from 'src/app/models/service-subscription';
import { NotificationService } from 'src/app/services/notification-service';
import { ServiceSubscriptionsService } from 'src/app/services/service-subscriptions.service';

@Component({
    selector: 'app-subscriptions',
    imports: [MatExpansionModule, MatButtonModule, NgClass, FormsModule, ReactiveFormsModule, MatCheckboxModule],
    templateUrl: './subscriptions.component.html',
    styleUrl: './subscriptions.component.scss',
})
export class SubscriptionsComponent implements OnInit {
    public ServiceCodes = ServiceCodes;

    public formGroup: UntypedFormGroup;

    public isModified: boolean = false;

    public originalSubscriptions: ServiceSubscription[];
    public updatedSubscriptions: ServiceSubscription[];

    constructor(
        private serviceSubscriptionsService: ServiceSubscriptionsService,
        private spinner: NgxSpinnerService,
        protected notificationService: NotificationService
    ) {
        this.formGroup = new UntypedFormGroup({
            hubPowerQuery: new FormControl<boolean>(false),
            hubApiApplication: new FormControl<boolean>(false),
            hubDashBoard: new FormControl<boolean>(false),
            hubEncryption: new FormControl<boolean>(false),
            hubHdsEncryption: new FormControl<boolean>(false),
            hubTchat: new FormControl<boolean>(false),
            hubSmsSending: new FormControl<boolean>(false),
        });
    }

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        this.load();
    }

    private load() {
        this.spinner.show('main-spinner');

        this.serviceSubscriptionsService.List().subscribe({
            next: (subscriptions) => {
                this.originalSubscriptions = subscriptions;
                this.updatedSubscriptions = ServiceSubscription.createDeepCopyOfArray(subscriptions);

                this.formGroup.get('hubPowerQuery').patchValue(subscriptions.find((x) => x.serviceCode === ServiceCodes.HubPowerQuery).enabled);
                this.formGroup.get('hubApiApplication').patchValue(subscriptions.find((x) => x.serviceCode === ServiceCodes.HubApiApplication).enabled);
                this.formGroup.get('hubDashBoard').patchValue(subscriptions.find((x) => x.serviceCode === ServiceCodes.HubDashBoard).enabled);
                this.formGroup.get('hubEncryption').patchValue(subscriptions.find((x) => x.serviceCode === ServiceCodes.HubEncryption).enabled);
                this.formGroup.get('hubHdsEncryption').patchValue(subscriptions.find((x) => x.serviceCode === ServiceCodes.HubHdsEncryption).enabled);
                this.formGroup.get('hubTchat').patchValue(subscriptions.find((x) => x.serviceCode === ServiceCodes.HubTchat).enabled);
                this.formGroup.get('hubSmsSending').patchValue(subscriptions.find((x) => x.serviceCode === ServiceCodes.HubSmsSending).enabled);

                this.spinner.hide('main-spinner');
            },
            error: (error) => {
                this.notificationService.displayError('Erreur', "Impossible de récupérer les abonnements de l'organisation." + error.message);

                this.spinner.hide('main-spinner');
            },
        });
    }

    /**
     * Appelée lorsque l'utilisateur enregistre les modifications apportées aux abonnements.
     */
    public save(): void {
        let request$: Observable<ServiceSubscription>[] = [];

        this.updatedSubscriptions.forEach((updated) => {
            const original = this.originalSubscriptions.find((value) => value.serviceCode === updated.serviceCode);

            if (updated.enabled !== original.enabled) {
                request$.push(this.serviceSubscriptionsService.update(updated.id, updated));
            }
        });

        if (request$.length > 0) {
            this.spinner.show('main-spinner');

            forkJoin(request$).subscribe({
                next: (subscriptions) => {
                    subscriptions.forEach((subscription) => {
                        let index = this.originalSubscriptions.findIndex((value) => value.id === subscription.id);
                        this.originalSubscriptions.splice(index, 1, subscription);
                        this.updatedSubscriptions = ServiceSubscription.createDeepCopyOfArray(this.originalSubscriptions);
                    });

                    this.spinner.hide('main-spinner');
                    this.isModified = false;
                },
                error: (error) => {
                    this.notificationService.displayError('Erreur', "Impossible de metre à jour les abonnements de l'organisation." + error.message);

                    this.spinner.hide('main-spinner');
                },
            });
        }
    }

    /**
     * Appelée lorsque l'utilisateur annule les modifications apportées aux abonnements.
     */
    public cancel() {
        this.load();
        this.isModified = false;
    }

    public onCheckboxChanged(serviceCode: ServiceCodes, event: MatCheckboxChange): void {
        let subscription = this.updatedSubscriptions.find((x) => x.serviceCode === serviceCode);

        if (subscription) {
            subscription.enabled = event.checked;
        }

        const originalSubscriptionsJSON = JSON.stringify(this.originalSubscriptions);
        const updatedSubscriptionsJSON = JSON.stringify(this.updatedSubscriptions);

        if (originalSubscriptionsJSON !== updatedSubscriptionsJSON) {
            this.isModified = true;
        } else {
            this.isModified = false;
        }
    }
}
